import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AdminPage } from "@/components/admin/AdminPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { KeyRound, Trash2 } from "lucide-react";

const ROLE_LABEL = { admin: "Workspace Admin", project_coordinator: "Project Coordinator", project_manager: "Project Manager", team_member: "Team Member / Designer", erp_operator: "ERP Operator", viewer: "Viewer", key_account: "Key Account", user: "User" };
const ROLE_CLS = { admin: "bg-[#2563EB] text-white", project_coordinator: "bg-indigo-100 text-indigo-700", project_manager: "bg-blue-100 text-blue-700", team_member: "bg-violet-100 text-violet-700", erp_operator: "bg-emerald-100 text-emerald-700", viewer: "bg-slate-100 text-slate-600", key_account: "bg-violet-100 text-violet-700", user: "bg-slate-100 text-slate-600" };

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [data, setData] = useState({ users: [], roles: [] });
  const [invites, setInvites] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "team_member" });
  const [lastLink, setLastLink] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const load = () => Promise.all([api.get("/admin/users").then((r) => setData(r.data)), api.get("/admin/invites").then((r) => setInvites(r.data))]).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendInvite = async () => {
    try {
      const { data: inv } = await api.post("/admin/invites", invite);
      setInvite({ email: "", role: "team_member" }); load();
      if (inv.type === "added") {
        setLastLink(null);
        toast.success(inv.message || "Existing user added to this workspace");
      } else {
        setLastLink(inv.link);
        toast.success("Invitation created — share the link with the invitee");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const revoke = async (inv) => { await api.delete(`/admin/invites/${inv.invite_id}`); load(); };

  const patch = async (u, body, msg) => {
    try { await api.patch(`/admin/users/${u.user_id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetPassword = (u) => {
    const pwd = window.prompt(`New password for ${u.name} (min 6 chars)`);
    if (pwd) patch(u, { password: pwd }, "Password updated");
  };
  const removeMember = async (u) => {
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success(`${u.name} removed from the workspace`); setDelUser(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminPage title="Users & Roles" subtitle="Invite-only workspace. Invite members, assign roles, and deactivate or remove accounts. Admin + ERP Operator can process order requests; Viewers are read-only." testid="admin-users-page">
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="invite-card">
        <h2 className="text-base font-semibold text-slate-900">Invite a member</h2>
        <p className="text-xs text-slate-500">New people join through an invitation link. If the email already belongs to an Airava account, they're added to this workspace right away — no link needed.</p>
        <div className="mt-3 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px]"><Label className="text-xs">Email</Label><Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="colleague@company.com" data-testid="invite-email-input" /></div>
          <div className="w-52"><Label className="text-xs">Role</Label>
            <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}><SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent></Select></div>
          <Button onClick={sendInvite} disabled={!invite.email} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="invite-send-btn">Create invitation</Button>
        </div>
        {lastLink && <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="invite-link"><span className="font-semibold text-emerald-800">Invitation link (email delivery not configured — copy and share it):</span><div className="font-mono break-all mt-1 text-slate-800">{lastLink}</div></div>}
        {invites.filter((i) => !i.accepted_at).length > 0 && (
          <div className="mt-3 divide-y divide-slate-100 text-xs">
            {invites.filter((i) => !i.accepted_at).map((i) => (
              <div key={i.invite_id} className="py-2 flex items-center gap-3" data-testid={`invite-row-${i.invite_id}`}>
                <span className="font-medium text-slate-800">{i.email}</span><Badge variant="outline" className="text-[10px]">{ROLE_LABEL[i.role] || i.role}</Badge>
                <span className="text-slate-400">invited by {i.invited_by_name}</span>
                <button onClick={() => revoke(i)} className="ml-auto text-red-600 hover:underline" data-testid={`invite-revoke-${i.invite_id}`}>Revoke</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50"><TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`} className={u.active === false ? "opacity-60" : ""}>
                <TableCell><div className="text-sm font-medium text-slate-900">{u.name}{u.user_id === me.user_id && <span className="ml-2 text-[10px] text-slate-400">(you)</span>}</div><div className="text-xs text-slate-500">{u.email}</div></TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => patch(u, { role: v }, `${u.name} is now ${ROLE_LABEL[v]}`)}>
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`user-role-select-${u.user_id}`}><SelectValue><Badge className={`${ROLE_CLS[u.role]} hover:bg-opacity-100`}>{ROLE_LABEL[u.role] || u.role}</Badge></SelectValue></SelectTrigger>
                    <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{u.manage_scope === "role_only" ? <span className="text-xs text-slate-400" title="This member's account is managed in another workspace">—</span> : <Switch checked={u.active !== false} onCheckedChange={(v) => patch(u, { active: v }, v ? "User activated" : "User deactivated")} data-testid={`user-active-switch-${u.user_id}`} />}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.auth_provider === "password" && u.manage_scope !== "role_only" && <Button size="sm" variant="ghost" onClick={() => resetPassword(u)} data-testid={`user-reset-password-${u.user_id}`}><KeyRound className="h-4 w-4 mr-1" />Password</Button>}
                    {u.user_id !== me.user_id && <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDelUser(u)} data-testid={`user-remove-${u.user_id}`}><Trash2 className="h-4 w-4 mr-1" />Remove</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <AlertDialogContent data-testid="user-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this member?</AlertDialogTitle>
            <AlertDialogDescription data-testid="user-remove-desc">
              {delUser && (delUser.manage_scope === "role_only"
                ? <><strong>{delUser.name}</strong> ({delUser.email}) will be removed from this workspace. They keep their Airava account and access to their other workspaces. Their projects, documents, notes and audit history here are preserved.</>
                : <><strong>{delUser.name}</strong> ({delUser.email}) will lose access to this workspace and will no longer be able to sign in. Their projects, documents, notes and audit history are preserved. This cannot be undone — you would need to re-invite them to restore access.</>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="user-remove-confirm" onClick={() => removeMember(delUser)}>Remove member</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
}
